import { Button, CircularProgress } from '@shared/components';
import { ContextMenuOption } from '@shared/components/button/Button';
import { useFeedContext } from '@shared/plugin/contexts/FeedContext';
import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import { ReactNode, useMemo, useState } from 'react';
import { MdOpenInNew } from 'react-icons/md';
import {
  PiArrowBendDoubleUpLeftBold,
  PiArrowBendUpLeftBold,
  PiCurrencyDollarBold,
  PiFilePlusBold,
  PiTextOutdent,
  PiTrayBold,
  PiXBold,
} from 'react-icons/pi';

import clsx from 'clsx';
import {
  SuggestedAction,
  SuggestedActionsFeedItem,
  SuggestedActionType,
} from '../../types';

const ROOT = makeRootClassName('SuggestedActions');
const el = makeElementClassNameFactory(ROOT);

type SuggestedActionItemProps = {
  item: SuggestedActionsFeedItem;
  isCompact?: boolean;
};

const ActionChip = ({
  content,
  icon,
  handleClick,
  loading,
  isCompact,
  options,
  variant,
}: {
  content: string;
  icon: ReactNode;
  handleClick: () => void;
  loading: boolean;
  isCompact?: boolean;
  options?: ContextMenuOption[];
  variant?: 'primary' | 'secondary';
}) => {
  return (
    <Button
      variant={variant || 'secondary'}
      size={isCompact ? 'xs' : 'small'}
      icon={loading ? undefined : icon}
      loadingIndicator={
        loading ? (
          <CircularProgress size="2xs" className={el`action-loading`} />
        ) : undefined
      }
      onPress={handleClick}
      isDisabled={loading}
      className={isCompact ? undefined : el`action-button`}
      contextMenuOptions={options}
    >
      {content}
    </Button>
  );
};

const ActionChipContainer = ({
  content,
  icon,
  action,
  isCompact,
  options,
  variant,
}: {
  content: string;
  icon: ReactNode;
  action: SuggestedAction;
  isCompact?: boolean;
  options?: ContextMenuOption[];
  variant?: 'primary' | 'secondary';
}) => {
  const {
    feed: { executeAction },
  } = useFeedContext();
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    executeAction(action).finally(() => {
      setLoading(false);
    });
  };

  return (
    <ActionChip
      variant={variant}
      isCompact={isCompact}
      content={content}
      icon={icon}
      handleClick={handleClick}
      loading={loading}
      options={options}
    />
  );
};

const DEFAULT_ACTIONS_COUNT = 3;

export const SuggestedActionsItem = ({
  item,
  isCompact,
}: SuggestedActionItemProps) => {
  const {
    hasUnconfirmedQuote,
    hasUnconfirmedOrder,
    feed: { executeAction },
  } = useFeedContext();
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const renderAction = (action: SuggestedAction) => {
    switch (action.type) {
      case SuggestedActionType.BUILD_ORDER:
        return (
          <ActionChipContainer
            variant={hasUnconfirmedOrder ? 'primary' : 'secondary'}
            action={action}
            isCompact={isCompact}
            content="Build order"
            icon={<PiTrayBold size={14} className={clsx(el`action-icon`)} />}
          />
        );
      case SuggestedActionType.BUILD_QUOTE:
        return (
          // TODO(parlato): Add the ability to "create with hint"
          <ActionChipContainer
            variant={hasUnconfirmedQuote ? 'primary' : 'secondary'}
            action={action}
            isCompact={isCompact}
            content="Create quote"
            icon={
              <PiCurrencyDollarBold
                size={14}
                className={clsx(el`action-icon`)}
              />
            }
            options={[
              {
                label: 'Create quote with hint',
                callback: () => {
                  executeAction({
                    type: SuggestedActionType.BUILD_QUOTE_WITH_HINT,
                  });
                },
              },
            ]}
          />
        );
      case SuggestedActionType.BUILD_REMAINING_ORDERS:
        return (
          <ActionChipContainer
            action={action}
            isCompact={isCompact}
            content="Build remaining loads"
            icon={
              <PiFilePlusBold size={14} className={clsx(el`action-icon`)} />
            }
          />
        );
      case SuggestedActionType.DRAFT_QUOTE_REPLY:
        return (
          <ActionChipContainer
            action={action}
            isCompact={isCompact}
            content="Draft quote reply"
            icon={
              <PiArrowBendUpLeftBold
                size={14}
                className={clsx(el`action-icon`)}
              />
            }
          />
        );
      case SuggestedActionType.BATCH_QUOTE_REPLY:
        return (
          <ActionChipContainer
            action={action}
            isCompact={isCompact}
            content="Batch reply"
            icon={
              <PiArrowBendDoubleUpLeftBold
                size={14}
                className={clsx(el`action-icon`)}
              />
            }
          />
        );
      case SuggestedActionType.CANCEL_BATCH_QUOTE_REPLY:
        return (
          <ActionChipContainer
            action={action}
            isCompact={isCompact}
            content="Cancel reply"
            icon={<PiXBold size={14} className={clsx(el`action-icon`)} />}
          />
        );
      case SuggestedActionType.OPEN_LINK:
        return (
          <ActionChipContainer
            action={action}
            isCompact={isCompact}
            content={action.title}
            icon={<MdOpenInNew size={14} className={clsx(el`action-icon`)} />}
          />
        );
      case SuggestedActionType.MARK_QUOTE_QUOTED:
        return (
          <ActionChipContainer
            action={action}
            isCompact={isCompact}
            content={'Mark quote quoted'}
            icon={<PiTextOutdent size={14} className={clsx(el`action-icon`)} />}
          />
        );
      case SuggestedActionType.MARK_QUOTE_WON:
        return (
          <ActionChipContainer
            action={action}
            isCompact={isCompact}
            content={'Mark quote won'}
            icon={
              <PiTextOutdent
                size={14}
                className={clsx(el`action-icon`, 'text-green-600')}
              />
            }
          />
        );
      default:
        throw new Error(`Unknown action: ${(action as any).type}`);
    }
  };

  const actions = useMemo(() => {
    if (showAllSuggestions) {
      return item.data.actions;
    }

    return item.data.actions.slice(0, DEFAULT_ACTIONS_COUNT);
  }, [item.data.actions, showAllSuggestions]);

  return (
    <div className={el`suggested-actions-root`}>
      {actions.map((action, idx) => {
        return (
          <div key={`suggested-action-${idx}`} className={el`action-container`}>
            {renderAction(action)}
          </div>
        );
      })}
      {item.data.actions.length > DEFAULT_ACTIONS_COUNT && (
        <Button
          variant="tertiary"
          size="small"
          onPress={() => setShowAllSuggestions((c) => !c)}
          className={el`show-more-button`}
        >
          {showAllSuggestions ? 'Less' : 'More...'}
        </Button>
      )}
    </div>
  );
};
